interface BarChartProps {
  data: Array<{ label: string; value: number; color?: string }>;
  height?: number;
  formatValue?: (n: number) => string;
  color?: string;
}

export function BarChart({ data, height = 120, formatValue = (n) => n.toString(), color = 'var(--primary)' }: BarChartProps) {
  if (!data.length || data.every((d) => d.value === 0)) {
    return <div className="empty" style={{ padding: 20, fontSize: 13 }}>Ma'lumot yo'q</div>;
  }
  const max = Math.max(...data.map((d) => d.value), 1);
  const barWidth = 100 / data.length;
  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          height,
          gap: 2,
          padding: '0 2px',
        }}
      >
        {data.map((d, i) => {
          const h = (d.value / max) * 100;
          return (
            <div
              key={i}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                height: '100%',
                position: 'relative',
              }}
              title={`${d.label}: ${formatValue(d.value)}`}
            >
              {d.value > 0 && (
                <div
                  style={{
                    fontSize: 9,
                    color: 'var(--text-dim)',
                    marginBottom: 2,
                  }}
                >
                  {d.value >= 1000 ? `${Math.round(d.value / 100) / 10}k` : d.value}
                </div>
              )}
              <div
                style={{
                  width: '100%',
                  height: `${h}%`,
                  background: d.color ?? color,
                  borderRadius: '3px 3px 0 0',
                  minHeight: d.value > 0 ? 2 : 0,
                  transition: 'height 0.3s ease',
                }}
              />
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 2, padding: '4px 2px 0' }}>
        {data.map((d, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              fontSize: 10,
              color: 'var(--text-dim)',
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {d.label}
          </div>
        ))}
      </div>
    </div>
  );
}

interface LineChartProps {
  data: Array<{ label: string; value: number }>;
  height?: number;
  color?: string;
  formatValue?: (n: number) => string;
}

export function LineChart({ data, height = 140, color = 'var(--primary)', formatValue = (n) => n.toString() }: LineChartProps) {
  if (!data.length || data.every((d) => d.value === 0)) {
    return <div className="empty" style={{ padding: 20, fontSize: 13 }}>Ma'lumot yo'q</div>;
  }
  const max = Math.max(...data.map((d) => d.value), 1);
  const min = 0;
  const range = max - min || 1;
  const w = 320;
  const h = height;
  const padX = 8;
  const padY = 16;
  const chartW = w - padX * 2;
  const chartH = h - padY * 2;

  const points = data.map((d, i) => {
    const x = padX + (i / Math.max(1, data.length - 1)) * chartW;
    const y = padY + chartH - ((d.value - min) / range) * chartH;
    return { x, y, value: d.value, label: d.label };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = `${pathD} L ${points[points.length - 1]!.x} ${padY + chartH} L ${points[0]!.x} ${padY + chartH} Z`;
  const last = points[points.length - 1]!;

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height, display: 'block' }}>
        <defs>
          <linearGradient id="line-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaD} fill="url(#line-grad)" />
        <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="2.5" fill={color} />
        ))}
        <text x={last.x} y={last.y - 8} textAnchor="end" fontSize="11" fontWeight="600" fill={color}>
          {formatValue(last.value)}
        </text>
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 8px', fontSize: 10, color: 'var(--text-dim)' }}>
        <span>{data[0]?.label}</span>
        <span>{data[data.length - 1]?.label}</span>
      </div>
    </div>
  );
}

interface PieProps {
  segments: Array<{ label: string; value: number; color: string }>;
}

export function PieChart({ segments }: PieProps) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  if (total === 0) {
    return <div className="empty" style={{ padding: 20, fontSize: 13 }}>Ma'lumot yo'q</div>;
  }
  let cumulative = 0;
  const radius = 50;
  const inner = 28;

  return (
    <div className="row" style={{ alignItems: 'center', gap: 16 }}>
      <svg viewBox="-60 -60 120 120" width={140} height={140}>
        {segments.map((s, i) => {
          if (s.value === 0) return null;
          const start = (cumulative / total) * Math.PI * 2;
          cumulative += s.value;
          const end = (cumulative / total) * Math.PI * 2;
          const large = end - start > Math.PI ? 1 : 0;
          const x1 = Math.sin(start) * radius;
          const y1 = -Math.cos(start) * radius;
          const x2 = Math.sin(end) * radius;
          const y2 = -Math.cos(end) * radius;
          const ix1 = Math.sin(end) * inner;
          const iy1 = -Math.cos(end) * inner;
          const ix2 = Math.sin(start) * inner;
          const iy2 = -Math.cos(start) * inner;
          const d = [
            `M ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2}`,
            `L ${ix1} ${iy1}`,
            `A ${inner} ${inner} 0 ${large} 0 ${ix2} ${iy2}`,
            'Z',
          ].join(' ');
          return <path key={i} d={d} fill={s.color} />;
        })}
      </svg>
      <div className="col" style={{ flex: 1, gap: 6 }}>
        {segments.map((s, i) => (
          <div key={i} className="row" style={{ fontSize: 12 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: s.color,
                display: 'inline-block',
              }}
            />
            <span style={{ marginLeft: 4 }}>{s.label}</span>
            <span className="muted" style={{ marginLeft: 'auto' }}>
              {s.value} ({Math.round((s.value / total) * 100)}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
